import {
  Box,
  ComponentMultiStyleConfig,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import React, { ReactElement, useMemo } from "react";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import { hasValue, isNullish } from "../../../lib/util/common-util";

/**
 * ラジオボタン表示用のデータクラス
 */
export type RadioContent = {
  options: RadioOption[];
};

export type RadioOption = {
  label: string;
  value: string;
};

export type CMFormInputBaseRadioProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "diff"; // エラーモードは非対応。
  size?: "sm" | "md" | "lg";

  /**
   * ラジオボタンの表示方向
   */
  direction?: "row" | "column";

  /**
   * ラジオボタン表示用のデータ
   */
  content: RadioContent;

  /**
   * 値。
   */
  value?: string | null;

  /**
   * 差分モード時の変更前の値。
   * 差分モードの場合のみ必須。
   */
  changedFrom?: string | null;

  onChange?: (value: string) => void;
  onBlur?: () => void;

  /**
   * ラジオボタン選択によって挿入する要素
   */
  insertedNodeOnSelected?: ReactElement;

  /**
   * insertedNodeOnSelectedをどのラジオボタンに紐づけるかに使用する。
   */
  insertValue?: string;
} & ComponentStyleProps;

/**
 * Radioコンポーネント.
 *
 * 差分モードでは、変更後の選択肢に枠を表示する。
 * エラーモード非対応。
 */
export const CMFormInputBaseRadio = ({
  editMode = "editable",
  variant = "default",
  size = "md",
  direction = "column",

  content,
  value = null,

  changedFrom = null,

  onChange,
  onBlur,

  insertValue,
  insertedNodeOnSelected,

  sx,
  ...rest
}: CMFormInputBaseRadioProps) => {
  const styles = useMultiStyleConfig("CMFormInputBaseRadioStyles", {
    variant,
    size,
  });

  const disabled = editMode === "disabled" || editMode === "readOnly";
  const isDiffMode = useMemo(() => variant === "diff", [variant]);

  // // 参照用Inputエリアのスタイル設定
  // const appliedLabelStyles = useMemo(
  //   () => ({
  //     ...styles.containerReadOnlyBase,
  //     ...(editMode === "readOnly" ? styles.containerReadOnly : undefined),
  //     ...(editMode === "readOnly" && shrinkOnReadOnly
  //       ? styles.containerReadOnlyShrunk
  //       : undefined),
  //   }),
  //   [styles, editMode, shrinkOnReadOnly],
  // );
  //

  const labelOfSelectedValue = useMemo(() => {
    if (isNullish(value)) return "";
    const option = content.options.find((o) => o.value === value);
    return hasValue(option) ? option.label : "";
  }, [content.options, value]);

  /**
   * 差分モード時、値が変更前と異なるならtrue。
   * 以下のいずれかの場合はfalse。
   * - 差分モードではない。
   * - 変更前と値が同一である。
   */
  const hasChangedInDiffMode = useMemo(
    () => isDiffMode && value !== changedFrom,
    [isDiffMode, value, changedFrom],
  );

  /**
   * 差分モード時、各選択肢の選択状態が変更前と異なるならtrueを返す。
   * 以下のいずれかの場合はfalseを返す。
   * - 差分モードではない。
   * - 変更前とチェック状態が同一である。
   *
   * @param v 判定対象の値。
   * @return 異なる場合はtrue。
   */
  const hasChangedInDiffModePerOption: (v: string) => boolean = (v) => {
    if (!isDiffMode) return false;

    const isSelectedBefore = v === changedFrom;
    const isSelectedNow = v === value;

    return isSelectedBefore !== isSelectedNow;
  };

  const readonlyStyles = useMemo(
    () => ({
      ...styles.readonly,
      ...(hasChangedInDiffMode ? styles.readonlyDiff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );

  return (
    <>
      {editMode === "readOnly" ? (
        <Text sx={readonlyStyles} {...rest}>
          {labelOfSelectedValue}
        </Text>
      ) : (
        <RadioGroup
          value={value ?? undefined}
          onChange={onChange}
          onBlur={onBlur}
          sx={sx}
          {...rest}
        >
          <Stack direction={direction} spacing={"15px"}>
            {content.options.map((option, index) => (
              <VStack key={index} spacing={0} alignItems={"flex-start"}>
                <Radio
                  isDisabled={disabled}
                  key={index}
                  value={option.value}
                  sx={styles.radioButton}
                >
                  <Text
                    sx={
                      hasChangedInDiffModePerOption(option.value)
                        ? { ...styles.text, ...styles.selectDiffValue }
                        : styles.text
                    }
                  >
                    {option.label}
                  </Text>
                </Radio>
                {option.value === insertValue && option.value === value && (
                  <Box>{insertedNodeOnSelected}</Box>
                )}
              </VStack>
            ))}
          </Stack>
        </RadioGroup>
      )}
    </>
  );
};

export const CMFormInputBaseRadioStyles: ComponentMultiStyleConfig = {
  parts: [
    "text",
    "selectDiffValue",
    "radioButton",
    "containerReadOnlyBase",
    "containerReadOnly",
    "containerReadOnlyShrunk",
    "readonly",
    "readonlyDiff",
  ],
  baseStyle: {
    text: {},
    containerReadOnlyBase: {},
    containerReadOnly: {
      cursor: "default",
      _disabled: {
        opacity: 1,
      },
    },
    containerReadOnlyShrunk: {},
    radioButton: {
      bgColor: "white",
    },
    readonly: {
      maxW: "max-content",
    },
    readonlyDiff: {
      borderWidth: "1px",
      borderColor: "orange.500",
    },
  },
  variants: {
    default: {
      text: {
        textAlign: "left",
      },

      selectDiffValue: {},
      containerReadOnlyBase: {},
      containerReadOnly: {
        border: "0",
      },
      containerReadOnlyShrunk: {},
    },
    error: {
      text: {
        borderWidth: "1px",
        borderColor: "transparent",
        textAlign: "left",
      },
      selectDiffValue: {
        borderWidth: "1px",
        borderColor: "red.500",
        borderRadius: "4px",
      },
      containerReadOnlyBase: {},
      containerReadOnly: {},
      containerReadOnlyShrunk: {},
    },
    diff: {
      text: {
        borderWidth: "1px",
        borderColor: "transparent",
        textAlign: "left",
      },
      selectDiffValue: {
        borderWidth: "1px",
        borderColor: "orange.500",
        borderRadius: "4px",
      },
      containerReadOnlyBase: {
        borderWidth: "1px",
        borderColor: "orange.500",
        _hover: {
          borderColor: "orange.500",
        },
        _focus: {
          _hover: {
            borderColor: "blue.500",
          },
        },
      },
      containerReadOnly: {},
      containerReadOnlyShrunk: {},
    },
  },
  sizes: {
    sm: {
      text: {
        // h: "22px",
        px: "6px",
        lineHeight: "22px",
      },
      radioButton: {
        h: "14px",
        w: "14px",
      },
      containerReadOnlyBase: {
        h: "22px",
        px: "6px",
      },
      containerReadOnly: {},
      containerReadOnlyShrunk: {
        h: "18px",
      },
    },
    md: {
      text: {
        // h: "24px",
        px: "8px",
        lineHeight: "24px",
      },
      radioButton: {},
      containerReadOnlyBase: {
        h: "30px",
        px: "8px",
      },
      containerReadOnly: {},
      containerReadOnlyShrunk: {
        h: "24px",
      },
    },
    lg: {
      text: {
        // h: "38px",
        px: "10px",
        lineHeight: "38px",
      },
      radioButton: {
        h: "20px",
        w: "20px",
      },
      lg: {
        containerReadOnlyBase: {
          h: "38px",
          px: "10px",
        },
        containerReadOnly: {},
        containerReadOnlyShrunk: {
          h: "30px",
        },
      },
    },
  },
};
